import { Key } from 'react'
import {
  getBeamOrientation,
  getOrthoOrientation,
  getMountingStructurePositions,
} from '~/components/DrawingCanvas/MountingStructures/util'
import { MetadataFields } from '~/components/MetadataFields'
import { useSelectedRoofSectionContext } from '~/components/Panels/SelectedRoofSectionPanel/hooks/useSelectedRoofSectionContext'
import { TABS, formatOptionsWithId, NUMBERS } from '~/components/Panels/SelectedRoofSectionPanel/util'
import {
  primaryToSecondaryTypes,
  primaryTypeToStyle,
  primaryMountingTypes,
  structureStyles,
  beamDirections,
} from '~/config/mountingStructures'
import { useAppSelector } from '~/store/hooks'
import { RoofSection } from '~/store/objects/types'
import { DimensionInput } from '~/ui/DimensionInput'
import { Select, SelectItem } from '~/ui/Select'
import { Switch } from '~/ui/Switch'
import { TabPanel } from '~/ui/Tabs'
import { TextField } from '~/ui/TextField'

export const PrimaryStructuresTabPanel = () => {
  const { selectedRoofSection, handleUpdate } = useSelectedRoofSectionContext()
  const {
    primaryStructureType,
    structureStyle,
    primaryStructuresEnabled,
    beamRotation,
    beamDepth,
    columnNames,
    beamWidth,
    beamSpacing,
    perimeterPoints,
    columnSpacing,
  } = selectedRoofSection
  const beamDirection = beamRotation === 0 ? 'vertical' : 'horizontal'
  const isLocked = useAppSelector(state => state.layers.layers.ROOF_SECTIONS.locked)

  const handleChangeRotation = (direction: Key | null) => {
    const rotation = direction === 'vertical' ? 0 : Math.PI / 2
    const beamOrientation = getBeamOrientation(rotation)
    const orthoOrientation = getOrthoOrientation(beamOrientation)
    const beamModels = getMountingStructurePositions(perimeterPoints, beamOrientation, beamSpacing)
    const columnLineModels = getMountingStructurePositions(perimeterPoints, orthoOrientation, columnSpacing)
    handleUpdate({ beamRotation: rotation, beamModels, columnLineModels })
  }

  const handleChangePrimaryType = (type: Key | null) => {
    const primaryStructureType = type as RoofSection['primaryStructureType']
    const secondaryStructureType = primaryToSecondaryTypes[primaryStructureType]?.[0]?.value ?? undefined
    const structureStyle = primaryTypeToStyle[primaryStructureType]
    handleUpdate({ primaryStructureType, secondaryStructureType, structureStyle })
  }

  return (
    <TabPanel className="p-1 space-y-3" id={TABS.primary}>
      <Switch
        isDisabled={isLocked}
        className="p-1 text-xs"
        isSelected={primaryStructuresEnabled}
        onChange={selected => handleUpdate({ primaryStructuresEnabled: selected })}
      >
        Primary Structures Enabled
      </Switch>
      <Select
        className="overflow-hidden"
        label="Type"
        isDisabled={isLocked}
        selectedKey={primaryStructureType}
        onSelectionChange={handleChangePrimaryType}
        items={formatOptionsWithId(primaryMountingTypes)}
      >
        {mountingType => <SelectItem value={mountingType}>{mountingType.value}</SelectItem>}
      </Select>
      <Select
        className="overflow-hidden"
        label="Style"
        isDisabled={isLocked}
        selectedKey={structureStyle}
        onSelectionChange={style => handleUpdate({ structureStyle: style as RoofSection['structureStyle'] })}
        items={formatOptionsWithId(structureStyles)}
      >
        {style => <SelectItem value={style}>{style.value}</SelectItem>}
      </Select>
      <Select
        className="overflow-hidden"
        label="Direction"
        isDisabled={isLocked}
        selectedKey={beamDirection}
        onSelectionChange={handleChangeRotation}
        items={formatOptionsWithId(beamDirections)}
      >
        {direction => <SelectItem value={direction}>{direction.value}</SelectItem>}
      </Select>
      <DimensionInput
        showStepperButtons
        isDisabled={isLocked}
        label="Beam Width"
        value={beamWidth}
        onCommit={value => handleUpdate({ beamWidth: value })}
      />
      <DimensionInput
        showStepperButtons
        isDisabled={isLocked}
        label="Beam Spacing"
        value={beamSpacing}
        onCommit={value =>
          handleUpdate({
            beamSpacing: value,
            beamModels: getMountingStructurePositions(perimeterPoints, getBeamOrientation(beamRotation), value),
          })
        }
      />
      <DimensionInput
        showStepperButtons
        isDisabled={isLocked}
        label="Beam Depth"
        value={beamDepth}
        onCommit={value => handleUpdate({ beamDepth: value })}
      />
      <TextField
        isDisabled={isLocked}
        label="Beam Labels"
        value={columnNames.join(',')}
        onChange={value => handleUpdate({ columnNames: (value || NUMBERS).split(',') })}
        description="Enter a list to be used for your beam labels, EX: A1, B1, C1"
      />
      <MetadataFields
        onChange={(fieldName, newValue) =>
          handleUpdate({ metadata: { ...(selectedRoofSection.metadata ?? {}), [fieldName]: newValue } })
        }
        onDeleteImage={updatedRoofSection => handleUpdate(updatedRoofSection)}
        selectedObjects={[selectedRoofSection]}
      />
    </TabPanel>
  )
}
